import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { toast } from 'react-toastify';

import { TachoOrderFormValues } from '../models/TachoOrderModel';
import { createTachoOrder, updateTachoOrder } from '../services/TachoOrderCRUD';

type Props = {
  mode: 'create' | 'edit';
  orderId?: number;
  initialValues?: TachoOrderFormValues;
};

const defaultValues: TachoOrderFormValues = {
  number: '',
  date: '',
  email: '',
  shipping_name: '',
  shipping_street: '',
  shipping_city: '',
  shipping_postcode: '',
  shipping_country: '',
  shipping_phone: '',
};

export default function TachoOrderForm({
  mode,
  orderId,
  initialValues,
}: Props) {
  const intl = useIntl();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    number: Yup.string().required(
      intl.formatMessage({ id: 'tachoOrders.fieldRequired' })
    ),
    date: Yup.string().nullable(),
    email: Yup.string()
      .email(intl.formatMessage({ id: 'tachoOrders.invalidEmail' }))
      .nullable(),
    shipping_name: Yup.string().required(
      intl.formatMessage({ id: 'tachoOrders.fieldRequired' })
    ),
    shipping_street: Yup.string().required(
      intl.formatMessage({ id: 'tachoOrders.fieldRequired' })
    ),
    shipping_city: Yup.string().required(
      intl.formatMessage({ id: 'tachoOrders.fieldRequired' })
    ),
    shipping_postcode: Yup.string().required(
      intl.formatMessage({ id: 'tachoOrders.fieldRequired' })
    ),
    shipping_phone: Yup.string().required(
      intl.formatMessage({ id: 'tachoOrders.fieldRequired' })
    ),
  });

  const formik = useFormik({
    initialValues: initialValues || defaultValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setLoading(true);
      try {
        const payload = {
          ...values,
          date: values.date || null,
          email: values.email || null,
          shipping_country: values.shipping_country || undefined,
        } as any;
        if (!payload.shipping_country) {
          delete payload.shipping_country;
        }

        if (mode === 'create') {
          await createTachoOrder(payload);
          toast.success(
            intl.formatMessage({ id: 'tachoOrders.createSuccess' })
          );
        } else if (orderId) {
          await updateTachoOrder(orderId, payload);
          toast.success(
            intl.formatMessage({ id: 'tachoOrders.updateSuccess' })
          );
        }
        history.push('/tacho-orders');
      } catch (error: any) {
        const detail = error?.response?.data;
        if (detail && typeof detail === 'object') {
          const messages = Object.values(detail).flat().join(' ');
          setStatus(messages);
        } else {
          setStatus(
            intl.formatMessage({
              id:
                mode === 'create'
                  ? 'tachoOrders.createError'
                  : 'tachoOrders.updateError',
            })
          );
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const renderField = (
    name: keyof TachoOrderFormValues,
    labelId: string,
    type: string = 'text',
    required: boolean = true
  ) => (
    <div className="col-md-6 mb-6">
      <label className={clsx('form-label fw-bold', required && 'required')}>
        {intl.formatMessage({ id: labelId })}
      </label>
      <input
        type={type}
        className={clsx(
          'form-control',
          formik.touched[name] && formik.errors[name] && 'is-invalid'
        )}
        {...formik.getFieldProps(name)}
      />
      {formik.touched[name] && formik.errors[name] && (
        <div className="invalid-feedback">{formik.errors[name]}</div>
      )}
    </div>
  );

  return (
    <div className="card p-10" style={{ maxWidth: 720 }}>
      <h2 className="card-title mb-8">
        {intl.formatMessage({
          id:
            mode === 'create'
              ? 'tachoOrders.createTitle'
              : 'tachoOrders.editTitle',
        })}
      </h2>

      {formik.status && (
        <div className="alert alert-danger mb-8">{formik.status}</div>
      )}

      <form onSubmit={formik.handleSubmit} noValidate>
        <div className="row">
          {renderField('number', 'tachoOrders.orderNumber', 'text', true)}
          {renderField('date', 'tachoOrders.date', 'date', false)}
        </div>

        <div className="row">
          {renderField('email', 'tachoOrders.email', 'email', false)}
          {renderField(
            'shipping_phone',
            'tachoOrders.shippingPhone',
            'tel',
            true
          )}
        </div>

        <h4 className="mt-4 mb-6">
          {intl.formatMessage({ id: 'tachoOrders.shippingAddressSection' })}
        </h4>

        <div className="row">
          {renderField(
            'shipping_name',
            'tachoOrders.shippingName',
            'text',
            true
          )}
          {renderField(
            'shipping_street',
            'tachoOrders.shippingStreet',
            'text',
            true
          )}
        </div>

        <div className="row">
          {renderField(
            'shipping_city',
            'tachoOrders.shippingCity',
            'text',
            true
          )}
          {renderField(
            'shipping_postcode',
            'tachoOrders.shippingPostcode',
            'text',
            true
          )}
        </div>

        <div className="d-flex gap-4 mt-8">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formik.isValid}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {intl.formatMessage({ id: 'tachoOrders.saving' })}
              </>
            ) : (
              intl.formatMessage({ id: 'tachoOrders.save' })
            )}
          </button>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => history.push('/tacho-orders')}
          >
            {intl.formatMessage({ id: 'tachoOrders.backToList' })}
          </button>
        </div>
      </form>
    </div>
  );
}
